import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router
} from '@angular/router';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { SessionService } from '@auth/auth';
import { bootstrapData } from 'src/bootstrap/bootstrap-data';
import { MfeAppConfig } from '../app.environment';
import { RouteService } from '../services/route/route.service';
import { ShellLoggingService } from '../services/shell-logging/shell-logging.service';
import { ShellService } from '../services/shell.service';
import { LoadedMfeHelper } from '../services/loaded-mfe.helper';
import { EnvironmentService } from '../services/environment/environment.service';
import { arrayToDictionary } from '../utils/utils';
import { UserSwService } from '../services/userSw/user-sw.service';
import { APP_CONST } from '@assets/const/app.const';
import { StylesService } from '../services/styles.service';
import { LoaderService } from '../services/loader.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroy$ = new Subject<void>();

  @Output() logout = new EventEmitter<void>();
  @Output() mfeSelected = new EventEmitter<string>();
  user$!: Observable<any>;

  mfeApps: MfeAppConfig[] = [];
  selectedMfeApp: MfeAppConfig | undefined;

  // Flags
  displayHeader = true;
  displayMenu = false;
  displayMobile = false;
  loadingMFE = false;

  constructor(
    @Inject('ShellService') private shellService: ShellService,
    private environmentService: EnvironmentService,
    private sessionService: SessionService,
    private shellLoggingService: ShellLoggingService,
    private router: Router,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private userSw: UserSwService,
    private readonly stylesService: StylesService,
    private readonly renderer: Renderer2
  ) {
    this.updateNavBasedOnWidth(window.innerWidth);
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        tap((event) => {
          if (event instanceof NavigationStart) {
            this.loadingMFE = true;
          } else if (
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel
          ) {
            this.loadingMFE = false;
          }
        })
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          const path = event.url.split('/')[1];
          const mfeAppForPath = !path
            ? undefined
            : this.environmentService
                .getAppsArray()
                .find((x) => x.path === path);
          if (this.environmentService.get('useRootPath') && !mfeAppForPath) {
            this.selectedMfeApp = this.routeService.getRootMFE();
          } else {
            this.selectedMfeApp = mfeAppForPath;
          }

          this.displayHeader = !event.url.includes('terms-and-conditions');
        }
      });
    this.user$ = this.shellLoggingService.getUser().pipe(
      takeUntil(this.destroy$),
      tap((user: any) => {
        const userAttributes: {
          attribute: string;
          attributeValue: string[];
        }[] = user['ccpUserAttributes']?.userAttributes;
        if (!userAttributes) {
          return;
        }

        const applicationsMap = arrayToDictionary(
          userAttributes
            .find((userAtt) => userAtt.attribute === 'PLATFORM_APPS')
            ?.attributeValue?.map((x) => x.toLowerCase()) ?? [],
          (x) => x
        );

        const mfeApps: MfeAppConfig[] = [];

        for (const [
          appKey,
          mfeApp
        ] of this.environmentService.getAppEntries()) {
          const appKeyToUse = mfeApp?.name || appKey;
          if (
            !mfeApp ||
            bootstrapData.mfeStates[mfeApp.path] === 'error-loading-module' ||
            !applicationsMap.has(appKeyToUse.toLowerCase()) ||
            !mfeApp.title
          ) {
            continue;
          }

          mfeApps.push(mfeApp);
        }

        this.displayMenu = mfeApps.length > 1;

        this.stylesService.applySubmenuStyleFix(mfeApps);
        this.stylesService.applyColorStyles(mfeApps, this.renderer);

        mfeApps.sort((a, b) => a.order - b.order);
        this.mfeApps = mfeApps;
      })
    );
  }

  ngAfterViewInit(): void {
    this.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.addMFEPortals();
    });
  }

  private addMFEPortals(): void {
    setTimeout(() => {
      const searchMfeContainer = document.getElementById(
        'search-mfe-container'
      );
      const quickReportsMfeContainer = document.getElementById(
        'header-suffix-mfe-container'
      );
      const searchBarDisplayed = document.getElementsByClassName('search-bar');
      if (
        searchBarDisplayed.length <= 0 &&
        searchMfeContainer &&
        quickReportsMfeContainer &&
        this.mfeApps.length > 0 &&
        !this.loadingMFE
      ) {
        this.shellService.headerStatusSubject.next(true);
      } else {
        this.addMFEPortals();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  loginWithRedirect(): void {
    this.sessionService.login({
      appState: {
        target: this.environmentService.get('baseUrl')
      }
    });
  }

  onLogOut(): void {
    this.userSw.clearUser();
    const appName = sessionStorage.getItem(APP_CONST.LAST_MFE_PATH) ?? '';

    const path = this.environmentService.getAppByKey(appName)?.path ?? '';
    const currentLocation = `${window.location.origin}/${path}`;
    this.sessionService.logout({
      logoutParams: {
        returnTo: currentLocation
      }
    });

    // Remove the loaded app from the session storage
    LoadedMfeHelper.clearLoadedApp();
  }

  onMfeSelected(mfeApp: MfeAppConfig) {
    this.loaderService.displayLoaderSubject.next(true);
    // Apply necessary changes only when the user is switching apps, not redirecting to the app's main page (e.g., CLU Dashboard).
    this.router.navigate([mfeApp.path]);
    const lastMfePath = sessionStorage.getItem(APP_CONST.LAST_MFE_PATH);
    if (mfeApp.path !== lastMfePath) {
      this.stylesService.removeAdditionalCSS(mfeApp.path);
      this.stylesService.loadCss(mfeApp.path, '');
    }
    this.selectedMfeApp = mfeApp;
    this.mfeSelected.emit(mfeApp.path);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateNavBasedOnWidth(event.target.innerWidth);
  }

  private updateNavBasedOnWidth(width: number): void {
    this.displayMobile = width < APP_CONST.MOBILE_THRESHOLD_WIDTH;
  }
}
